import { useState } from 'react'
import { Link } from 'react-router-dom'
import { HOME_QUESTIONS, HOME_QUESTIONS_EN } from './questions'
import { track } from '@/lib/analytics'
import { useLocale } from '@/i18n/locale'

/**
 * FAQ главной — шесть вопросов, которые снимают главные возражения
 * собственника: время, 152-ФЗ, галлюцинации, санкции, договор, люди.
 * Состав и порядок — HOME_INDEXES в ./questions.ts, полный список — на /faq.
 *
 * Содержание FAQ — в ./questions.ts (один источник для секции и schema.org/FAQPage).
 * На главной — шестёрка HOME_QUESTIONS(_EN); та же шестёрка уходит в FAQPage
 * (Seo.tsx), чтобы разметка не описывала невидимое. Остальные пять — на /faq.
 */

const COPY = {
  ru: {
    eyebrow: 'Частые вопросы',
    heading: 'Шесть вопросов до договора',
    lede: 'Перед договором собственники спрашивают об одном и том же. Отвечаем коротко и без оговорок мелким шрифтом.',
    all: 'Все 11 вопросов',
  },
  en: {
    eyebrow: 'FAQ',
    heading: 'Six questions before signing',
    lede: 'Before signing, owners ask the same things. We answer briefly, with no fine print.',
    all: '',
  },
}

export function Faq() {
  const locale = useLocale()
  const t = COPY[locale]
  const questions = locale === 'en' ? HOME_QUESTIONS_EN : HOME_QUESTIONS
  const [open, setOpen] = useState<number | null>(0)

  return (
    <section id="faq" className="section bg-card">
      {/* Заголовок отдельной колонкой слева: аккордеон в 768px посреди
          экрана в 1920 выглядел как случайно оставленная колонка текста.
          Раскладывать сами вопросы в две колонки нельзя — раскрытие
          одного сдвигало бы соседний, и человек терял бы место. */}
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 xl:gap-16 items-start">
        <div className="lg:col-span-4 lg:sticky lg:top-24">
          <div className="font-mono text-small uppercase tracking-wider text-muted mb-4">{t.eyebrow}</div>
          <h2 className="text-h2 text-ink text-balance">{t.heading}</h2>
          <p className="mt-6 text-body text-muted">{t.lede}</p>
        </div>

        <div className="lg:col-span-8 border-t border-divider">
          {questions.map((item, i) => {
            const isOpen = open === i
            return (
              <div key={item.q} className="border-b border-divider">
                <button
                  type="button"
                  onClick={() => {
                    const next = isOpen ? null : i
                    setOpen(next)
                    if (next !== null) {
                      // question_id = индекс в массиве; стабилен, пока порядок не меняется
                      track('faq_open', { question_id: i })
                    }
                  }}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-6 py-6 text-left"
                >
                  <span className="text-body font-medium text-ink">{item.q}</span>
                  <span aria-hidden className="font-mono text-h3 text-muted leading-none">{isOpen ? '–' : '+'}</span>
                </button>
                {isOpen && (
                  <div className="pb-6 text-body text-muted">
                    {item.a}
                    {item.live && (
                      <span className="block mt-3 text-small text-ink border-l-2 border-accent pl-4">
                        {item.live}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )
          })}
          {/* /faq — ru-only: на /en ссылку не рендерим, чтобы не выкидывать
              англоязычного посетителя на русскую страницу. */}
          {locale === 'ru' && (
            <div className="mt-8">
              <Link
                to="/faq"
                className="font-mono text-small font-medium text-ink hover:text-accent no-underline transition-colors"
              >
                {t.all} <span aria-hidden>→</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
